// frontend/components/Diagnostics/DiagnosticsPage.jsx
//
// Self-serve diagnostic page. Auto-runs all connectivity probes on mount,
// displays results as they come in, and lets the user save a structured
// report to the backend (viewable in admin) and/or copy a summary for support.
//
// Design: each probe runs independently. A failure in one probe never blocks
// the others. The page loads without a live socket (the socket may be broken
// — that's usually why the user opened this page). All probe results are
// compiled into a single report that's sent via plain HTTP POST (never socket).

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, ScrollView, StyleSheet, View, ActivityIndicator, Pressable } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import MetaHead from '@/components/MetaHead';
import Icon from '@/components/Icon';
import { API_HOST } from '@/constants/config';
import {
    collectClientInfo,
    probeHttpEcho,
    probeStreaming,
    probeAuth,
    probeIsolatedSocket,
    snapshotSingletonSocket,
    startTimelineCapture,
    getBundleBuildId,
} from '@/lib/diagnostics/probes';
import { probeServiceWorker, clearCacheAndUpdateSW, unregisterServiceWorker } from '@/lib/diagnostics/swProbe';

const STATUS = { IDLE: 'idle', RUNNING: 'running', DONE: 'done' };

export default function DiagnosticsPage() {
    const { theme } = useTheme();
    const insets = useSafeAreaInsets();

    const [running, setRunning] = useState(false);
    const [results, setResults] = useState({});
    const [savedRef, setSavedRef] = useState(null);
    const [statusMsg, setStatusMsg] = useState(null); // { type: 'success'|'error', text: string }
    const [saving, setSaving] = useState(false);
    const timelineRef = useRef(null);
    const resultsRef = useRef({});

    const runAll = useCallback(async () => {
        setRunning(true);
        setSavedRef(null);
        setResults({});

        // Start timeline capture before any probes run.
        if (timelineRef.current) timelineRef.current.stop();
        timelineRef.current = startTimelineCapture();

        // Run probes. Each is independent — wrap in try/catch so one failure
        // never blocks the others.
        const probes = [
            ['clientInfo', async () => ({ clientInfo: collectClientInfo() })],
            ['httpProbe', async () => {
                const r = await probeHttpEcho();
                return {
                    httpProbe: {
                        ok: r.ok, status: r.status, durationMs: r.durationMs,
                        serverTimeSkewMs: r.serverTimeSkewMs,
                        clientIp: r.serverData?.clientIp, error: r.error,
                    },
                    // Cloudflare edge context mirrored from the echo so the
                    // page can display the user's real IPv6 alongside the
                    // synthetic pseudo-IPv4 bogon. The POST below captures
                    // these again server-side (authoritative).
                    cfHeaders: r.cf || null,
                    serverHealth: r.serverData?.process || {},
                };
            }],
            ['streamProbe', async () => ({ streamProbe: await probeStreaming() })],
            ['authProbe', async () => ({ authProbe: await probeAuth() })],
            ['socketProbe', async () => ({ socketProbe: await probeIsolatedSocket() })],
            ['singletonSnapshot', async () => ({ singletonSnapshot: snapshotSingletonSocket() })],
            ['swProbe', async () => ({ swProbe: await probeServiceWorker() })],
        ];

        const collected = {};
        for (const [key, fn] of probes) {
            setResults((prev) => ({ ...prev, [key]: { _status: STATUS.RUNNING } }));
            try {
                const r = await fn();
                Object.assign(collected, r);
                setResults((prev) => ({ ...prev, [key]: { ...r[key] || r, _status: STATUS.DONE } }));
            } catch (err) {
                const errResult = { error: err?.message || String(err), _status: STATUS.DONE };
                collected[key] = errResult;
                setResults((prev) => ({ ...prev, [key]: errResult }));
            }
        }

        // Capture build IDs from the SW probe (or fall back to bundle-only on native).
        const swProbeResult = collected.swProbe || {};
        collected.buildIds = swProbeResult.buildIds || { bundle: getBundleBuildId(), origin: null, sw: null };

        // Store the full compiled data for saving.
        resultsRef.current = collected;
        setRunning(false);
    }, []);

    // Auto-run on mount.
    useEffect(() => {
        runAll();
        return () => {
            if (timelineRef.current) timelineRef.current.stop();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSaveAndCopy = useCallback(async () => {
        setSaving(true);
        let ref = null;

        // 1. Save the report to the backend.
        try {
            const { useAuthStore } = require('@/store');
            const token = useAuthStore.getState().token;
            const payload = {
                ...resultsRef.current,
                clientInfo: resultsRef.current.clientInfo || collectClientInfo(),
                timeline: timelineRef.current?.events || [],
                notes: '',
                raw: { ...resultsRef.current, timestamp: new Date().toISOString() },
            };

            const res = await fetch(`${API_HOST}/json/diagnostics/reports`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify(payload),
            });
            const data = await res.json();

            if (data.ok) {
                ref = data.ref;
                setSavedRef(ref);
            } else {
                setStatusMsg({ type: 'error', text: 'Failed to save report to server. Summary copied instead.' });
            }
        } catch (err) {
            setStatusMsg({ type: 'error', text: 'Failed to save report: ' + (err?.message || 'unknown error') });
        }

        // 2. Build the summary (with ref if save succeeded) and copy to clipboard.
        const summary = buildTextSummary(resultsRef.current, timelineRef.current?.events || [], ref);
        try {
            if (Platform.OS === 'web' && navigator?.clipboard) {
                await navigator.clipboard.writeText(summary);
            }
            if (ref) {
                setStatusMsg({ type: 'success', text: `Report saved (${ref}) and copied to clipboard. Paste it to support.` });
            }
        } catch (_) {
            if (ref) {
                setStatusMsg({ type: 'success', text: `Report saved (${ref}). Copy failed — please copy manually.` });
            }
        }

        setSaving(false);
    }, []);

    const styles = getStyles(theme);

    return (
        <ScrollView style={styles.container} contentContainerStyle={{ paddingBottom: insets.bottom + 40 }}>
            <MetaHead title="Connection Diagnostics — Gab AI" />

            <View style={styles.header}>
                <Text style={styles.title}>Connection Diagnostics</Text>
                <Text style={styles.subtitle}>
                    Testing all connections. If something fails, save a report and send it to support.
                </Text>
            </View>

            <ProbeCard theme={theme} title="Network Status" icon="wifi" result={results.clientInfo} render={(d) => (
                <>
                    <ProbeRow label="Online" value={d?.online === true ? 'Connected' : d?.online === false ? 'Offline' : 'Unknown'} pass={d?.online === true} theme={theme} />
                    {d?.networkType && <ProbeRow label="Connection type" value={d.networkType} theme={theme} />}
                    {d?.downlink != null && <ProbeRow label="Downlink" value={`${d.downlink} Mbps`} theme={theme} />}
                    {d?.rtt != null && <ProbeRow label="Round-trip time" value={`${d.rtt} ms`} pass={d.rtt < 500} theme={theme} />}
                    {d?.saveData && <ProbeRow label="Save-Data" value="Enabled (data saver)" theme={theme} />}
                    {d?.platform && <ProbeRow label="Platform" value={`${d.platform}${d.browser ? ' / ' + d.browser : ''}${d.os ? ' / ' + d.os : ''}`} theme={theme} />}
                </>
            )} />

            <ProbeCard theme={theme} title="Server Connection (HTTP)" icon="server" result={results.httpProbe} render={(d) => (
                <>
                    <ProbeRow label="Reachable" value={d?.ok ? 'Yes' : 'No'} pass={d?.ok} theme={theme} />
                    {d?.status != null && <ProbeRow label="HTTP status" value={String(d.status)} pass={d.status >= 200 && d.status < 300} theme={theme} />}
                    {d?.durationMs != null && <ProbeRow label="Response time" value={`${d.durationMs} ms`} pass={d.durationMs < 3000} theme={theme} />}
                    {d?.serverTimeSkewMs != null && <ProbeRow label="Clock skew" value={`${d.serverTimeSkewMs > 0 ? '+' : ''}${d.serverTimeSkewMs} ms`} pass={Math.abs(d.serverTimeSkewMs) < 60000} theme={theme} />}
                    {/* Real client IP (IPv6 for mobile users on 464XLAT carriers) vs the synthetic 240.x bogon. The real IP is what support needs to grep CF logs. */}
                    {results.cfHeaders?.connectingIp && (
                        <ProbeRow label="Real IP (CF)" value={results.cfHeaders.connectingIp} theme={theme} />
                    )}
                    {results.cfHeaders?.pseudoIpv4 && (
                        <ProbeRow
                            label="Pseudo IPv4"
                            value={results.cfHeaders.pseudoIpv4}
                            warn={/^(240\.|198\.1[89]\.)/.test(results.cfHeaders.pseudoIpv4)}
                            theme={theme}
                        />
                    )}
                    {results.cfHeaders?.ray && <ProbeRow label="CF Ray" value={results.cfHeaders.ray} theme={theme} />}
                    {results.cfHeaders?.colo && <ProbeRow label="CF Colo" value={results.cfHeaders.colo} theme={theme} />}
                    {results.cfHeaders?.ipcountry && <ProbeRow label="CF Country" value={results.cfHeaders.ipcountry} theme={theme} />}
                    {/* If CF is in pseudo-IPv4 "overwrite" mode, cf-connecting-ip is replaced with the bogon and the real IPv6 is not exposed anywhere. Surface that explicitly. */}
                    {results.cfHeaders?.connectingIp && results.cfHeaders?.pseudoIpv4 &&
                        results.cfHeaders.connectingIp === results.cfHeaders.pseudoIpv4 && (
                        <Text style={styles.hint}>
                            ⚠ Real IP equals pseudo IPv4 — your Cloudflare zone is in "Pseudo IPv4: Overwrite" mode. Switch to "Add header" so support can locate you in CF logs by your real IPv6.
                        </Text>
                    )}
                    {d?.error && <ProbeRow label="Error" value={d.error} pass={false} theme={theme} />}
                </>
            )} />

            <ProbeCard theme={theme} title="Streaming (HTTP/SSE)" icon="stream" result={results.streamProbe} render={(d) => (
                <>
                    {d?.buffered === true && (
                        <View style={{ backgroundColor: '#f59e0b22', borderRadius: 8, padding: 10, marginBottom: 8 }}>
                            <Text style={{ color: '#f59e0b', fontSize: 13, fontWeight: '600' }}>
                                ⚠ Response was buffered — chunks arrived in a burst instead of streaming. A proxy or carrier is holding the response. Voice mode and any streaming feature will be broken.
                            </Text>
                        </View>
                    )}
                    <ProbeRow label="Stream completed" value={d?.ok ? 'Yes' : 'No'} pass={d?.ok} theme={theme} />
                    {d?.status != null && <ProbeRow label="HTTP status" value={String(d.status)} pass={d.status >= 200 && d.status < 300} theme={theme} />}
                    {d?.chunksReceived != null && (
                        <ProbeRow
                            label="Chunks received"
                            value={`${d.chunksReceived} / ${d.chunksExpected ?? '?'}`}
                            pass={d.chunksReceived >= (d.chunksExpected ?? 5)}
                            theme={theme}
                        />
                    )}
                    {d?.firstByteMs != null && (
                        <ProbeRow
                            label="Time to first chunk"
                            value={`${d.firstByteMs} ms`}
                            pass={d.firstByteMs < 1000}
                            warn={d.firstByteMs >= 1000 && d.firstByteMs < 3000}
                            theme={theme}
                        />
                    )}
                    {d?.gapAvgMs != null && (
                        <ProbeRow
                            label="Avg gap between chunks"
                            value={`${d.gapAvgMs} ms`}
                            pass={d.gapAvgMs < 600}
                            warn={d.gapAvgMs >= 600 && d.gapAvgMs < 2000}
                            theme={theme}
                        />
                    )}
                    {d?.totalMs != null && <ProbeRow label="Total time" value={`${d.totalMs} ms`} theme={theme} />}
                    {d?.error && <ProbeRow label="Error" value={d.error} pass={false} theme={theme} />}
                </>
            )} />

            <ProbeCard theme={theme} title="Authentication" icon="key" result={results.authProbe} render={(d) => (
                <>
                    <ProbeRow label="Token present" value={d?.tokenPresent ? 'Yes' : 'No'} pass={d?.tokenPresent} theme={theme} />
                    {d?.tokenExpiredAt && <ProbeRow label="Token expires" value={d.tokenExpiredAt} theme={theme} />}
                    {d?.refreshOk != null && <ProbeRow label="Token refresh" value={d.refreshOk ? 'Working' : 'Failed'} pass={d.refreshOk} theme={theme} />}
                    {d?.guardedGetStatus != null && <ProbeRow label="Authenticated request" value={`HTTP ${d.guardedGetStatus}`} pass={d.guardedGetStatus >= 200 && d.guardedGetStatus < 300} theme={theme} />}
                    {d?.error && <ProbeRow label="Error" value={d.error} pass={false} theme={theme} />}
                </>
            )} />

            <ProbeCard theme={theme} title="Socket Connection" icon="plug" result={results.socketProbe} render={(d) => (
                <>
                    <ProbeRow label="Connected" value={d?.connected ? 'Yes' : 'No'} pass={d?.connected} theme={theme} />
                    {d?.transport && (
                        <ProbeRow
                            label="Transport"
                            value={d.transport}
                            // websocket: pass. polling WITH upgrade failure: fail
                            // (real problem). polling WITHOUT upgrade attempt
                            // visible: warn (degraded but might still upgrade).
                            pass={d.transport === 'websocket'}
                            warn={d.transport === 'polling' && !d?.upgradeError}
                            theme={theme}
                        />
                    )}
                    {d?.transportUpgraded === true && (
                        <ProbeRow label="Upgrade (polling → WS)" value="Succeeded" pass theme={theme} />
                    )}
                    {d?.transportUpgraded === false && d?.transport === 'polling' && (
                        <ProbeRow
                            label="Upgrade (polling → WS)"
                            value="Stuck on polling"
                            pass={false}
                            theme={theme}
                        />
                    )}
                    {d?.upgradeError && (
                        <ProbeRow label="Upgrade error" value={d.upgradeError} pass={false} theme={theme} />
                    )}
                    {d?.handshakeMs != null && <ProbeRow label="Handshake time" value={`${d.handshakeMs} ms`} pass={d.handshakeMs < 5000} theme={theme} />}
                    {d?.pingPongMs != null && <ProbeRow label="Round-trip latency" value={`${d.pingPongMs} ms`} pass={d.pingPongMs < 1000} theme={theme} />}
                    {d?.pingPongMs == null && d?.connected && (
                        <ProbeRow label="Round-trip latency" value="no pong received" warn theme={theme} />
                    )}
                    {d?.connectError && <ProbeRow label="Connect error" value={d.connectError} pass={false} theme={theme} />}
                </>
            )} />

            <ProbeCard theme={theme} title="Service Worker" icon="gear" result={results.swProbe} render={(d) => (
                <>
                    {d?.applicable === false ? (
                        <Text style={styles.hint}>{d.note}</Text>
                    ) : (
                        <>
                    {d?.versionSkew === true && (
                        <View style={{ backgroundColor: '#ef444422', borderRadius: 8, padding: 10, marginBottom: 8 }}>
                            <Text style={{ color: '#ef4444', fontSize: 13, fontWeight: '600' }}>
                                ⚠ Version skew detected — the service worker may be serving a stale build.
                            </Text>
                        </View>
                    )}
                    <ProbeRow label="Registered" value={d?.registered ? 'Yes' : 'No'} pass={d?.registered} theme={theme} />
                    {d?.scriptURL && <ProbeRow label="Script URL" value={d.scriptURL.replace(/^https?:\/\/[^/]+/, '')} theme={theme} />}
                    {d?.state && <ProbeRow label="State" value={d.state} pass={d.state === 'activated'} theme={theme} />}
                    {d?.scope && <ProbeRow label="Scope" value={d.scope} theme={theme} />}
                    {d?.buildIds && (
                        <>
                            <ProbeRow label="Bundle BUILD_ID" value={d.buildIds.bundle || 'not set'} theme={theme} />
                            <ProbeRow label="Origin BUILD_ID" value={d.buildIds.origin || 'not found'} pass={!!d.buildIds.origin && d.buildIds.origin === d.buildIds.bundle} theme={theme} />
                            <ProbeRow label="SW BUILD_ID" value={d.buildIds.sw || 'none remembered'} pass={!!d.buildIds.sw && d.buildIds.sw === d.buildIds.bundle} warn={d.buildIds.sw && d.buildIds.sw !== d.buildIds.bundle} theme={theme} />
                        </>
                    )}
                    {d?.originFetchOk === false && <ProbeRow label="/build.json" value="fetch failed" pass={false} theme={theme} />}
                    {d?.note && <Text style={styles.hint}>{d.note}</Text>}
                    {/* Fix actions — show when skew detected or SW not remembering */}
                    {(d?.versionSkew || (!d?.buildIds?.sw && d?.registered)) && (
                        <View style={{ flexDirection: 'row', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                            <Pressable
                                style={{ backgroundColor: '#3b82f6', paddingHorizontal: 12, paddingVertical: 8, borderRadius: 6 }}
                                onPress={() => clearCacheAndUpdateSW()}
                            >
                                <Text style={{ color: '#fff', fontSize: 13, fontWeight: '600' }}>Clear Cache & Update</Text>
                            </Pressable>
                            <Pressable
                                style={{ backgroundColor: '#ef4444', paddingHorizontal: 12, paddingVertical: 8, borderRadius: 6 }}
                                onPress={() => unregisterServiceWorker()}
                            >
                                <Text style={{ color: '#fff', fontSize: 13, fontWeight: '600' }}>Unregister SW</Text>
                            </Pressable>
                        </View>
                    )}
                        </>
                    )}
                </>
            )} />
            <View style={[styles.actionsRow, { paddingBottom: insets.bottom }]}>
                <Pressable style={[styles.btn, styles.btnPrimary]} onPress={handleSaveAndCopy} disabled={saving || running}>
                    {saving ? <ActivityIndicator color="#fff" size="small" /> : <Text style={styles.btnText}>Save & Copy</Text>}
                </Pressable>
                <Pressable style={[styles.btn, styles.btnSecondary]} onPress={runAll} disabled={running}>
                    <Icon name="rotate-right" size={16} color={theme === 'black' ? '#fff' : '#333'} />
                    <Text style={[styles.btnText, { color: theme === 'black' ? '#fff' : '#333' }]}>Re-run</Text>
                </Pressable>
            </View>

            {savedRef && (
                <View style={styles.savedBanner}>
                    <Icon name="circle-check" size={20} color="#22c55e" />
                    <Text style={styles.savedText}>
                        Report saved. Give this code to support: <Text style={styles.savedRef}>{savedRef}</Text>
                    </Text>
                </View>
            )}

            {statusMsg && !savedRef && (
                <View style={[styles.savedBanner, { borderColor: statusMsg.type === 'error' ? '#ef444433' : '#22c55e33', backgroundColor: statusMsg.type === 'error' ? (theme === 'black' ? '#1f0d0d' : '#fef2f2') : (theme === 'black' ? '#0d1f0d' : '#f0fdf4') }]}>
                    <Icon name={statusMsg.type === 'error' ? 'circle-exclamation' : 'circle-check'} size={20} color={statusMsg.type === 'error' ? '#ef4444' : '#22c55e'} />
                    <Text style={styles.savedText}>{statusMsg.text}</Text>
                </View>
            )}
        </ScrollView>
    );
}

// ---------------------------------------------------------------------------
// Probe card — renders a titled section with a status badge.
// ---------------------------------------------------------------------------
function ProbeCard({ theme, title, icon, result, render }) {
    const styles = getStyles(theme);
    const status = result?._status || STATUS.IDLE;
    const hasError = status === STATUS.DONE && result?.ok === false;
    const isRunning = status === STATUS.RUNNING;

    return (
        <View style={styles.card}>
            <View style={styles.cardHeader}>
                <View style={styles.cardTitleRow}>
                    {icon && <Icon name={icon} size={18} color="#8b8b8b" />}
                    <Text style={styles.cardTitle}>{title}</Text>
                </View>
                {isRunning && <ActivityIndicator size="small" color="#3b82f6" />}
                {status === STATUS.DONE && !hasError && result?.ok !== false && (
                    <Icon name="circle-check" size={20} color="#22c55e" />
                )}
                {hasError && <Icon name="circle-exclamation" size={20} color="#ef4444" />}
            </View>
            {status === STATUS.DONE && render(result)}
            {status === STATUS.RUNNING && <Text style={styles.pending}>Testing...</Text>}
            {status === STATUS.IDLE && <Text style={styles.pending}>Waiting...</Text>}
        </View>
    );
}

// ---------------------------------------------------------------------------
// Probe row — label/value pair with optional pass/fail coloring.
// ---------------------------------------------------------------------------
function ProbeRow({ label, value, pass, warn, theme }) {
    const styles = getStyles(theme);
    const color = warn ? '#f59e0b' : pass === true ? '#22c55e' : pass === false ? '#ef4444' : '#ccc';
    return (
        <View style={styles.row}>
            <Text style={styles.rowLabel}>{label}</Text>
            <Text style={[styles.rowValue, { color }]}>{value}</Text>
        </View>
    );
}

// ---------------------------------------------------------------------------
// Build a human-readable summary for clipboard / support handoff.
// ---------------------------------------------------------------------------
function buildTextSummary(results, timeline, ref) {
    const lines = ['=== Gab AI Diagnostic Report ==='];
    if (ref) lines.push(`Ref: ${ref}`);
    lines.push(`Time: ${new Date().toISOString()}`);
    const ci = results.clientInfo || {};
    const hp = results.httpProbe || {};
    const cf = results.cfHeaders || {};
    // Real CF client IP first — this is what support needs to grep CF logs.
    // The legacy clientIp from the echo is the pseudo-IPv4 bogon (240.x) when
    // CF is in overwrite mode, which is useless for log lookup.
    const realIp = cf.connectingIp || hp.serverData?.clientIp || hp.clientIp || '?';
    lines.push(`Real IP: ${realIp}`);
    if (cf.pseudoIpv4) lines.push(`Pseudo IPv4: ${cf.pseudoIpv4}`);
    if (cf.ray) lines.push(`CF Ray: ${cf.ray}`);
    if (cf.colo) lines.push(`CF Colo: ${cf.colo}`);
    if (cf.ipcountry) lines.push(`CF Country: ${cf.ipcountry}`);
    lines.push(`UA: ${ci.userAgent || '?'}`);
    lines.push(`Platform: ${ci.platform || '?'} / ${ci.os || '?'} / ${ci.browser || '?'}`);
    lines.push(`Online: ${ci.online} | Network: ${ci.networkType || '?'} | Downlink: ${ci.downlink || '?'} Mbps | RTT: ${ci.rtt || '?'} ms`);
    if (ci.timezone) lines.push(`Timezone: ${ci.timezone}`);
    if (ci.locale) lines.push(`Locale: ${ci.locale}`);
    lines.push('');
    lines.push(`HTTP: ${hp.ok ? 'OK' : 'FAILED'} status=${hp.status || '?'} time=${hp.durationMs || '?'}ms skew=${hp.serverTimeSkewMs || '?'}ms${hp.error ? ' err=' + hp.error : ''}`);
    const stp = results.streamProbe || {};
    lines.push(`Stream: ${stp.ok ? 'OK' : 'FAILED'} chunks=${stp.chunksReceived ?? '?'}/${stp.chunksExpected ?? '?'} firstByte=${stp.firstByteMs ?? '?'}ms gapAvg=${stp.gapAvgMs ?? '?'}ms total=${stp.totalMs ?? '?'}ms${stp.buffered ? ' ⚠ BUFFERED' : ''}${stp.error ? ' err=' + stp.error : ''}`);
    const ap = results.authProbe || {};
    lines.push(`Auth: token=${ap.tokenPresent ? 'present' : 'absent'} refresh=${ap.refreshOk} guarded=${ap.guardedGetStatus || '?'}${ap.error ? ' err=' + ap.error : ''}`);
    const sp = results.socketProbe || {};
    const upgradeInfo = sp.transportUpgraded === true
        ? '(upgraded)'
        : sp.transportUpgraded === false && sp.transport === 'polling'
            ? (sp.upgradeError ? `(upgrade failed: ${sp.upgradeError})` : '(stuck on polling)')
            : '';
    lines.push(`Socket: ${sp.connected ? 'connected' : 'FAILED'} transport=${sp.transport || '?'}${upgradeInfo ? ' ' + upgradeInfo : ''} handshake=${sp.handshakeMs || '?'}ms rtt=${sp.pingPongMs ?? '?'}ms${sp.connectError ? ' err=' + sp.connectError : ''}`);
    const ss = results.singletonSnapshot || {};
    lines.push(`App socket: connected=${ss.isSocketConnected} raw=${ss.rawSocketConnected} rateLimited=${ss.isRateLimited} transport=${ss.transport || '?'}`);
    const sw = results.swProbe || {};
    const bi = sw.buildIds || results.buildIds || {};
    if (bi.bundle || bi.origin || bi.sw) {
        lines.push(`Build: bundle=${bi.bundle || '?'} origin=${bi.origin || '?'} sw=${bi.sw || '?'}${sw.versionSkew ? ' ⚠ SKEW' : ''}`);
    }
    if (sw.registered != null) {
        lines.push(`SW: registered=${sw.registered} state=${sw.state || '?'}${sw.scriptURL ? ' url=' + sw.scriptURL.replace(/^https?:\/\/[^/]+/, '') : ''}`);
    }
    if (timeline && timeline.length) {
        lines.push('');
        lines.push(`Timeline (${timeline.length} events):`);
        for (const e of timeline.slice(-20)) {
            lines.push(`  ${e.t} ${e.type}${e.detail ? ': ' + e.detail : ''}`);
        }
    }
    return lines.join('\n');
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
function getStyles(theme) {
    const isDark = theme === 'black' || theme === 'dark';
    const bg = isDark ? '#0a0a0a' : '#fff';
    const cardBg = isDark ? '#161616' : '#f5f5f5';
    const borderColor = isDark ? '#222' : '#e0e0e0';
    const textColor = isDark ? '#e0e0e0' : '#1a1a1a';
    const subTextColor = isDark ? '#8b8b8b' : '#666';

    return StyleSheet.create({
        container: { flex: 1, backgroundColor: bg, paddingHorizontal: 20 },
        header: { paddingTop: 20, paddingBottom: 16 },
        title: { fontSize: 28, fontWeight: '700', color: textColor },
        subtitle: { fontSize: 14, color: subTextColor, marginTop: 6 },
        card: {
            backgroundColor: cardBg,
            borderRadius: 12,
            padding: 16,
            marginBottom: 12,
            borderWidth: 1,
            borderColor,
        },
        cardHeader: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
        },
        cardTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
        cardTitle: { fontSize: 16, fontWeight: '600', color: textColor },
        row: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingVertical: 4,
        },
        rowLabel: { fontSize: 14, color: subTextColor },
        rowValue: { fontSize: 14, fontWeight: '500', color: '#ccc' },
        pending: { fontSize: 13, color: subTextColor, fontStyle: 'italic' },
        hint: {
            fontSize: 12,
            color: subTextColor,
            marginTop: 8,
            lineHeight: 18,
        },
        actionsRow: {
            flexDirection: 'row',
            gap: 10,
            marginTop: 8,
            flexWrap: 'wrap',
        },
        btn: {
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6,
            paddingHorizontal: 16,
            paddingVertical: 10,
            borderRadius: 8,
        },
        btnPrimary: { backgroundColor: '#3b82f6' },
        btnSecondary: { backgroundColor: cardBg, borderWidth: 1, borderColor },
        btnText: { fontSize: 14, fontWeight: '600', color: '#fff' },
        savedBanner: {
            flexDirection: 'row',
            alignItems: 'center',
            gap: 10,
            backgroundColor: isDark ? '#0d1f0d' : '#f0fdf4',
            borderWidth: 1,
            borderColor: '#22c55e33',
            borderRadius: 10,
            padding: 14,
            marginTop: 12,
        },
        savedText: { fontSize: 13, color: textColor, flex: 1 },
        savedRef: { fontWeight: '700', fontFamily: 'monospace', fontSize: 15 },
    });
}
